import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { MailPlus, Plus, Pencil, Trash2, Loader2, Inbox, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Senders() {
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSender, setSelectedSender] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allowedDomain, setAllowedDomain] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    is_active: true
  });

  const fetchSenders = async () => {
    try {
      const response = await axios.get(`${API}/senders`);
      setSenders(response.data);
    } catch (error) {
      toast.error('Failed to load senders');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      setAllowedDomain(response.data.allowed_sender_domain);
    } catch (error) {
      console.error('Failed to fetch config');
    }
  };

  useEffect(() => {
    fetchSenders();
    fetchConfig();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', email: '', is_active: true });
    setSelectedSender(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (sender) => {
    setSelectedSender(sender);
    setFormData({
      name: sender.name,
      email: sender.email,
      is_active: sender.is_active
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (sender) => {
    setSelectedSender(sender);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedSender) {
        await axios.put(`${API}/senders/${selectedSender.id}`, formData);
        toast.success('Sender updated successfully');
      } else {
        await axios.post(`${API}/senders`, formData);
        toast.success('Sender created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchSenders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/senders/${selectedSender.id}`);
      toast.success('Sender deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedSender(null);
      fetchSenders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="senders-page">
        <div className="flex justify-between items-center">
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="senders-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sender Emails</h1>
          <p className="text-muted-foreground mt-1">
            Manage sender email addresses for your campaigns
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-sender-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Sender
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MailPlus className="w-5 h-5" />
            Configured Senders
          </CardTitle>
          <CardDescription>
            {senders.length} sender email{senders.length !== 1 ? 's' : ''} configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {senders.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <Inbox className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No senders configured</h3>
              <p className="text-muted-foreground mb-4">
                Add your first sender email to start sending emails
              </p>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Sender
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {senders.map(sender => (
                  <TableRow key={sender.id} data-testid={`sender-row-${sender.id}`}>
                    <TableCell className="font-medium">{sender.name}</TableCell>
                    <TableCell className="font-mono text-sm">{sender.email}</TableCell>
                    <TableCell>
                      <Badge variant={sender.is_active ? 'default' : 'secondary'}>
                        {sender.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(sender.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(sender)}
                        data-testid={`edit-sender-${sender.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(sender)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-sender-${sender.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedSender ? 'Edit Sender' : 'Add New Sender'}</DialogTitle>
            <DialogDescription>
              {selectedSender 
                ? 'Update the sender email details' 
                : 'Add a new sender email address for your campaigns'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Display Name *</Label>
              <Input
                id="name"
                placeholder="Company Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="sender-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder={allowedDomain ? `name@${allowedDomain}` : "noreply@company.com"}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="sender-email-input"
              />
              {allowedDomain && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Only emails from @{allowedDomain} are allowed
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="sender-active-switch"
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-sender-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedSender ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sender</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedSender?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-sender"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
